import React from 'react';

import { BaseCalendar } from './components/BaseCalendar';
import { SplitsCard } from './components/MobileCards/SplitsCard';
import { createSplitsColumns } from './columns/splitsColumns';
import { BaseCalendarPageProps, SplitItem } from './types';
import { applyDateFilter, applyWatchlistFilter, applyGenericFilter } from './utils/filterUtils';

const getSplitType = (numerator: number, denominator: number): string => {
  return numerator < denominator ? "Reverse Split" : "Forward Split";
};

const formatSplitRatio = (numerator: number, denominator: number): string => {
  return `${numerator}-for-${denominator}`;
};

const getSentiment = (numerator: number, denominator: number): string => {
  if (numerator < denominator) return "Bearish Bias";
  if (numerator > denominator) return "Bullish Bias";
  return "Neutral";
};

const SplitsCalendarPage: React.FC<BaseCalendarPageProps> = (props) => {
  const columnWidths = [8, 8, 10, 9, 10, 10];

  const transformData = (data: any[]) => {
    return data.flatMap((day) => {
      return day.items.map((item: SplitItem) => ({
        symbol: item.t,
        date: item.d,
        numerator: item.n,
        denominator: item.dn,
        marketCap: item.mc,
        splitDate: item.d,
        splitType: getSplitType(item.n, item.dn),
        splitRatio: formatSplitRatio(item.n, item.dn),
        sentiment: getSentiment(item.n, item.dn),
      }));
    });
  };

  const filterData = (transformedData: any[], filterOptions: any) => {
    const isTickerSpecific = filterOptions.selectedDate === null &&
      filterOptions.fromDate === null &&
      filterOptions.toDate === null &&
      filterOptions.selectedTimeframe === null;

    if (isTickerSpecific) {
      return transformedData;
    }

    let filteredItems = applyDateFilter(transformedData, filterOptions);

    if (!filterOptions.selectedThirdSectionFilters.has('all')) {
      filteredItems = applyGenericFilter(
        filteredItems,
        filterOptions.selectedThirdSectionFilters,
        'splitType',
        (item, filter) => {
          if (filter === 'Forward' && item.splitType === 'Forward Split') return true;
          if (filter === 'Reverse' && item.splitType === 'Reverse Split') return true;
          if (filter === 'forward' && item.splitType === 'Forward Split') return true;
          if (filter === 'reverse' && item.splitType === 'Reverse Split') return true;
          return false;
        }
      );
    }

    if (!filterOptions.selectedFourthSectionFilters.has('all')) {
      filteredItems = applyGenericFilter(
        filteredItems,
        filterOptions.selectedFourthSectionFilters,
        'sentiment',
        (item, filter) => {
          if (filter === 'bullish' && item.sentiment.includes('Bullish')) return true;
          if (filter === 'bearish' && item.sentiment.includes('Bearish')) return true;
          if (filter === 'neutral' && item.sentiment === 'Neutral') return true;
          return false;
        }
      );
    }

    return applyWatchlistFilter(
      filteredItems,
      filterOptions.isWatchListOnly,
      filterOptions.bookmarkedItems
    );
  };

  return (
    <BaseCalendar
      {...props}
      symbol={props?.symbol ?? ''}
      screenType="splits"
      title="Splits Calendar"
      columns={createSplitsColumns}
      columnWidths={columnWidths}
      mobileCard={SplitsCard}
      filterData={filterData}
      transformData={transformData}
    />
  );
};

export default SplitsCalendarPage;